import React, { useEffect, useState } from "react";
import { getDiscountTiers, setDiscountTiers } from "../services/api";

const emptyTier = { minQty: 1, percent: 0 };

const AdminDiscounts = () => {
  const [tiers, setTiers] = useState([]);
  const [minPhotos, setMinPhotos] = useState("1");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const fetchTiers = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await getDiscountTiers();
        const arr = Array.isArray(data) ? data : data?.tiers || [];
        setTiers(
          arr.map((t) => ({
            minQty: t?.minQty !== undefined ? String(t.minQty) : "",
            percent: t?.percent !== undefined ? String(t.percent) : "",
          }))
        );
        if (!Array.isArray(data) && data?.minPhotos !== undefined) {
          setMinPhotos(String(data.minPhotos));
        } else {
          setMinPhotos("1");
        }
      } catch (e) {
        setError("Não foi possível carregar descontos.");
      } finally {
        setLoading(false);
      }
    };
    fetchTiers();
  }, []);

  const addTier = () => {
    setTiers((prev) => [...prev, { minQty: "", percent: "" }]);
  };

  const removeTier = (idx) => {
    setTiers((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateTier = (idx, key, value) => {
    setTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, [key]: value } : t)));
  };

  const save = async () => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const minPhotosVal = Math.max(1, parseInt(minPhotos, 10) || 1);

      // validações apenas numéricas (sem impor faixas ou mínimos)
      const normalized = tiers.map((t) => ({
        minQty: Number(t.minQty),
        percent: Number(t.percent),
      }));

      for (const t of normalized) {
        if (!Number.isFinite(t.minQty) || !Number.isFinite(t.percent)) {
          throw new Error("Valores inválidos: use números para quantidade e percentual");
        }
      }

      const sorted = [...normalized].sort((a, b) => a.minQty - b.minQty);
      await setDiscountTiers({ tiers: sorted, minPhotos: minPhotosVal });
      setSuccess("Configurações salvas com sucesso.");
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container mx-auto p-4 max-w-3xl mt-16">
      <h1 className="text-2xl font-bold mb-4">Parâmetros de Descontos e Regras de Compra</h1>
      <p className="text-sm mb-6 opacity-80">
        Configure a quantidade mínima de fotos para habilitar a compra e os percentuais de desconto progressivo.
      </p>

      {loading ? (
        <div className="alert">Carregando...</div>
      ) : (
        <div className="space-y-6">
          {/* Parâmetro de Mínimo de Fotos */}
          <div className="bg-base-200 p-4 rounded-lg border border-base-300">
            <h2 className="text-base font-semibold mb-1">Mínimo de Fotos para Habilitar Compra</h2>
            <p className="text-xs opacity-75 mb-3">
              Quantidade mínima de fotos necessárias no carrinho para liberar a opção "Ir para o pagamento". Se for 1, não haverá limitação.
            </p>
            <div className="flex items-center gap-3">
              <label className="label text-sm font-medium">Qtd Mínima de Fotos:</label>
              <input
                type="number"
                min="1"
                className="input input-bordered w-28"
                value={minPhotos}
                onChange={(e) => setMinPhotos(e.target.value)}
              />
            </div>
          </div>

          {/* Faixas de Desconto */}
          <div>
            <h2 className="text-base font-semibold mb-2">Descontos Progressivos</h2>
            <div className="space-y-3">
              {tiers.map((t, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span className="badge badge-neutral">{idx + 1}</span>
                  <label className="label">Qtd mínima</label>
                  <input
                    type="text"
                    className="input input-bordered w-24"
                    value={t.minQty}
                    onChange={(e) => updateTier(idx, "minQty", e.target.value)}
                  />
                  <label className="label">Desconto (%)</label>
                  <input
                    type="text"
                    className="input input-bordered w-24"
                    value={t.percent}
                    onChange={(e) => updateTier(idx, "percent", e.target.value)}
                  />
                  <button className="btn btn-error btn-sm" onClick={() => removeTier(idx)}>
                    Remover
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 mt-4">
            <button className="btn" onClick={addTier}>
              Adicionar faixa
            </button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              Salvar
            </button>
          </div>

          {error && <div className="alert alert-error mt-4">{error}</div>}
          {success && <div className="alert alert-success mt-4">{success}</div>}

          <div className="mt-6 p-3 rounded bg-base-200">
            <h2 className="font-semibold mb-2">Sugestão de modelo</h2>
            <ul className="list-disc ml-6">
              <li>3+ fotos: 5%</li>
              <li>5+ fotos: 10%</li>
              <li>10+ fotos: 15%</li>
            </ul>
            <p className="text-xs opacity-70 mt-2">
              O maior tier aplicável será usado com base na quantidade total.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDiscounts;
